/**
 * Focus mode: work the Reply Later stack one message at a time.
 *
 * Keyboard-only by design (art bible 1.8): `j` and `k` move, `Enter` is
 * reply, `Escape` is stop. The bindings are printed on the surface, not
 * hidden behind a help modal.
 *
 * This file owns the plumbing: which message is on screen, what each key
 * does, and its own listener on `document`, registered on construction and
 * removed by `release`. Everything about the mail belongs to the caller: the
 * stack it reads, the surface it draws with, and the reply it opens.
 */
import { el, clear } from "./render";

export interface FocusOptions<T> {
  /**
   * The stack to work through, re-read on every paint, so a message answered
   * anywhere leaves the stack rather than staying until the session ends.
   */
  stack: () => readonly T[];
  /** Draw one message in the focus slot. Called by every paint. */
  render: (item: T) => HTMLElement;
  /** Answer the message on screen. Enter. */
  act: (item: T) => void;
  /** Leave focus mode: Escape, or a stack that ran empty. */
  stop: () => void;
  /** While true another surface owns the keys and this one is inert. */
  paused: () => boolean;
}

export interface FocusSession {
  /** One slot: the keys above it, the current message inside it. */
  readonly node: HTMLElement;
  /** Draw the message at `at`, clamped to the stack. Stops when it runs dry. */
  paint(at?: number): void;
  /** Handle one keydown, alone. True when the key was focus mode's. */
  handle(e: KeyboardEvent): boolean;
  /** Remove this session's keydown listener. Idempotent. */
  release(): void;
}

/** The bindings, held as data so the printout cannot drift from the handles. */
export const BINDINGS: readonly (readonly [key: string, does: string])[] = [
  ["j", "Next"],
  ["k", "Previous"],
  ["Enter", "Reply"],
  ["Esc", "Stop"],
];

/** Which element is doing the talking on a key press. */
function typing(e: KeyboardEvent): boolean {
  const target = e.target;
  return target instanceof Element
    && target.closest("input, textarea, select, [contenteditable='true']") !== null;
}

export function focusSession<T>(opts: FocusOptions<T>): FocusSession {
  let at = 0;

  const keys = el("div", { class: "focus-keys", "aria-hidden": "true" });
  for (const [key, does] of BINDINGS) {
    keys.append(el("kbd", {}, key), el("span", { class: "does" }, does));
  }
  const count = el("span", { class: "focus-count" });
  const slot = el("div", { class: "focus-slot" });
  const node = el(
    "section",
    { class: "focus", "aria-label": "Focus mode" },
    el("div", { class: "focus-head" }, count, keys),
    slot,
  );

  function clamp(next: number): readonly T[] {
    const stack = opts.stack();
    at = stack.length === 0 ? 0 : Math.min(Math.max(next, 0), stack.length - 1);
    return stack;
  }

  function leave(): void {
    release();
    opts.stop();
  }

  function handle(e: KeyboardEvent): boolean {
    // Another surface owns the keys, or the person is writing somewhere.
    if (opts.paused() || typing(e)) return false;
    // A modified key belongs to the browser, not to this list.
    if (e.ctrlKey || e.metaKey || e.altKey) return false;
    const stack = opts.stack();
    if (stack.length === 0) {
      leave();
      return true;
    }
    switch (e.key) {
      case "j":
        clamp(at + 1);
        paint();
        return true;
      case "k":
        clamp(at - 1);
        paint();
        return true;
      case "Enter":
        // Enter on a focused control is that control's press, not ours.
        if (e.target instanceof Element
          && e.target.closest("button, a, [role='button'], label, [onclick]") !== null
        ) return false;
        opts.act(clamp(at)[at]!);
        return true;
      case "Escape":
        leave();
        return true;
      default:
        return false;
    }
  }

  const onKey = (e: KeyboardEvent): void => { handle(e); };
  document.addEventListener("keydown", onKey);

  function paint(next = at): void {
    const stack = clamp(next);
    if (stack.length === 0) {
      leave();
      return;
    }
    count.textContent = `${at + 1} of ${stack.length}`;
    clear(slot);
    slot.append(opts.render(stack[at]!));
  }

  function release(): void {
    document.removeEventListener("keydown", onKey);
  }

  return { node, paint, handle, release };
}
